import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { BuslineDetailQueryResponse } from '@area-restrita/api-client/model/buslineDetailQueryResponse';
import { MapContainer } from 'react-leaflet';
import { Feature, GeoJsonProperties, LineString } from 'geojson';
import togeojson from '@mapbox/togeojson';
import JSZip from 'jszip';
import L from 'leaflet';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import FileUploader from '@/components/FileUploader';
import { FileWithPreview } from '@/hooks/useFileUpload';
import Combobox, { ComboboxOption } from '@/components/Combobox';
import InlineInput from '@/components/InlineInput';
import CargoTileLayer from '@/components/Map/CargoTileLayer';
import RouteMapLayer from '@/components/Map/RouteMapLayer';
import { lineStringToRoutePath } from '@/utils/lineStringToRoute';

import { IBuslineSchema } from '../../components/BuslineForm';

interface IProps {
  busline: BuslineDetailQueryResponse;

  control: Control<IBuslineSchema>;
}

const DEFAULT_CENTER: [number, number] = [-23.5505, -46.6333];
const DEFAULT_ZOOM = 16;

const extractKmlTextFromKmz = async (file: File) => {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const kmlEntry = Object.values(zip.files).find(
    (entry) => !entry.dir && entry.name.toLowerCase().endsWith('.kml'),
  );

  if (!kmlEntry) return null;

  return kmlEntry.async('text');
};

const RoutePreview: React.FC<IProps> = ({ control, busline }) => {
  const map = React.useRef<L.Map>(null);
  const { setValue, watch, setError, clearErrors } =
    useFormContext<IBuslineSchema>();

  const [lineStringOptions, setLineStringOptions] = useState<ComboboxOption[]>(
    [],
  );

  const parseFiles = useCallback(
    async (files: FileWithPreview[]) => {
      try {
        const file = files.at(0)?.file;
        if (!(file instanceof File)) {
          setLineStringOptions([]);
          setValue('lineStringSelected', undefined);
          return undefined;
        }

        const isKmz =
          file.name.toLowerCase().endsWith('.kmz') ||
          file.type === 'application/vnd.google-earth.kmz';

        const text = isKmz
          ? await extractKmlTextFromKmz(file)
          : await file.text();

        if (!text) {
          setError('lineStrings', {
            type: 'manual',
            message: 'Nenhum arquivo KML encontrado dentro do KMZ.',
          });
          setLineStringOptions([]);
          setValue('lineStringSelected', undefined);
          return undefined;
        }

        const dom = new DOMParser().parseFromString(text, 'text/xml');

        // Verificar erros de parsing XML
        if (dom.getElementsByTagName('parsererror').length > 0) {
          setError('lineStrings', {
            type: 'manual',
            message: 'Erro ao fazer parse do arquivo XML. Verifique o formato.',
          });
          setLineStringOptions([]);
          setValue('lineStringSelected', undefined);
          return undefined;
        }

        const geojson = togeojson.kml(dom);

        // Filtrar apenas features do tipo LineString
        const lineStringFeatures = geojson.features.filter(
          (feature): feature is Feature<LineString, GeoJsonProperties> =>
            feature.geometry.type === 'LineString',
        );

        if (lineStringFeatures.length === 0) {
          setError('lineStrings', {
            type: 'manual',
            message: 'Nenhuma linha encontrada no arquivo.',
          });
          setLineStringOptions([]);
          setValue('lineStringSelected', undefined);
          return undefined;
        }

        // Criar opções do combobox
        const options = lineStringFeatures.map((feature, index) => ({
          value: index,
          label: feature.properties?.name || `Linha ${index + 1}`,
        }));

        setLineStringOptions(options);

        // Selecionar primeira linha por padrão
        setValue('lineStringSelected', options[0]);

        // Limpar erros quando o parsing é bem-sucedido
        clearErrors('lineStrings');

        return lineStringFeatures.map((feature) => feature.geometry);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Erro ao processar arquivo';
        setError('lineStrings', {
          type: 'manual',
          message: `Erro: ${message}`,
        });
        setLineStringOptions([]);
        setValue('lineStringSelected', undefined);
        return undefined;
      }
    },
    [setValue, setError, clearErrors],
  );

  const lineStrings = watch('lineStrings') as LineString[] | undefined;
  const lineStringSelected = watch('lineStringSelected') as
    | ComboboxOption
    | undefined;

  // Extrair coordenadas itinerário
  const routeCoordinates = useMemo(
    () =>
      lineStringToRoutePath(
        lineStrings && lineStringSelected
          ? lineStrings.at(+lineStringSelected.value)
          : busline.route,
      ),
    [lineStrings, lineStringSelected, busline.route],
  );

  // Atualizar bounds do mapa quando as coordenadas do itinerário mudarem
  useEffect(() => {
    if (!routeCoordinates) return;

    const bounds = L.latLngBounds(
      L.latLng(routeCoordinates.start.lat, routeCoordinates.start.lng),
      L.latLng(routeCoordinates.end.lat, routeCoordinates.end.lng),
    );

    // Garantir que o ajuste de bounds ocorra após o mapa ser renderizado
    setImmediate(() => {
      map.current?.fitBounds(bounds, { padding: [50, 50] });
    });
  }, [routeCoordinates]);

  return (
    <Card className="flex flex-col flex-1 min-w-[450px]">
      <CardHeader>
        <CardTitle>Arquivo de Rota e Pré-visualização</CardTitle>
        <CardDescription>
          Faça upload do arquivo de rota para visualizar a rota no mapa.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 h-full">
        <FormField
          control={control}
          name="lineStrings"
          render={({ field: { onChange } }) => (
            <FormItem>
              <FormControl>
                <FileUploader
                  accept=".kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"
                  maxSize={10 * 1024 * 1024}
                  onFilesChange={async (files) => {
                    const result = await parseFiles(files);
                    onChange(result);
                  }}
                  hideDropzoneOnFileSelected
                  maxFiles={1}
                  label="Enviar arquivo KML ou KMZ"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {lineStrings && lineStringOptions.length > 0 && (
          <FormField
            control={control}
            name="lineStringSelected"
            render={({ field }) => (
              <InlineInput
                as={Combobox}
                title="Linha do arquivo"
                options={lineStringOptions}
                placeholder="Selecione uma linha do arquivo"
                emptyText="Nenhuma linha disponível no arquivo"
                value={field.value}
                onChangeValue={field.onChange}
              />
            )}
          />
        )}

        <div className="grid h-full w-full min-h-[400px] overflow-hidden rounded-xl border">
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom={true}
            style={{ height: '100%', zIndex: 0 }}
            ref={map}
          >
            <CargoTileLayer />
            {routeCoordinates && <RouteMapLayer route={routeCoordinates} />}
          </MapContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default RoutePreview;
